/**
 * QueryProvider — data-fetching com cache segmentado por tenant (DD-004, RNF 2.3, RNF 8).
 *
 * Envolve o `QueryClientProvider` do TanStack Query e provê o escopo de tenant/BU
 * corrente. Toda `queryKey` é prefixada por `tenantId`/`businessUnitId` via
 * `buildQueryKey` (data/queryKeys), isolando o cache por tenant.
 *
 * RNF 2.1/2.3 — Ao trocar de tenant, o cache do tenant anterior é descartado
 * (`queryClient.clear()`) ANTES de os filhos renderizarem com o novo escopo,
 * garantindo que dados de tenants distintos jamais coexistam no cache.
 */

import {
  QueryClient,
  QueryClientProvider,
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  createContext,
  useContext,
  useRef,
  type ReactNode,
} from "react";
import { buildQueryKey, type TenantQueryKey } from "../../data/queryKeys.js";

/** Escopo de tenant/BU corrente injetado nas chaves de cache. */
export interface TenantScope {
  tenantId: string;
  businessUnitId: string | null;
}

const TenantScopeContext = createContext<TenantScope | null>(null);

export function useTenantScope(): TenantScope {
  const scope = useContext(TenantScopeContext);
  if (!scope) {
    throw new Error("useTenantScope deve ser usado dentro de QueryProvider");
  }
  return scope;
}

/** QueryClient padrão do portal: retry conservador, sem refetch agressivo. */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });
}

export interface QueryProviderProps {
  scope: TenantScope;
  /** Filhos a renderizar dentro do contexto de tenant/query. */
  children?: ReactNode;
  /** Injeção de cliente para testes. Em produção, um cliente é criado uma vez. */
  client?: QueryClient;
}

export function QueryProvider({ scope, children, client }: QueryProviderProps) {
  const clientRef = useRef<QueryClient | null>(null);
  if (clientRef.current === null) {
    clientRef.current = client ?? createQueryClient();
  }
  const queryClient = clientRef.current;

  // Detecção de troca de TENANT em tempo de render (top-down): limpa o cache ANTES de
  // os filhos registrarem queries do novo escopo, garantindo que dados de tenants
  // distintos jamais coexistam no cache (RNF 2.3, DD-004).
  //
  // REGRESSÃO (achada em produção, 2026-09-08): esta condição também disparava em toda
  // troca de BU DENTRO do mesmo tenant — não só de tenant, como o comentário sempre
  // disse. `queryClient.clear()` não distingue chave por chave: ele apaga o cliente
  // INTEIRO, inclusive leituras tenant-wide que `useTenantQuery` também prefixa por
  // businessUnitId (`businessUnits`, `tenants`, `branding` — ver hooks.ts), mas que não
  // variam com a BU ativa. Em `PlatformPage`, essas duas consultas gateiam a página
  // inteira (`if (tenantQuery.isLoading || busQuery.isLoading) return <Carregando/>`) —
  // ao trocar de BU em Estágios ou Canais de origem, o clear() zerava esse cache, as
  // duas consultas voltavam a `isLoading`, `PlatformPage` desmontava `PlatformConfig`
  // por inteiro, e o `useState<Tab>("branding")` dele reinicializava no valor padrão ao
  // remontar — a troca de unidade "voltava para Branding" (já suspeitado e marcado
  // "NÃO REPRODUZIDO" numa revisão anterior; reproduzido de verdade em produção depois
  // do deploy do `platform-config-ux`). businessUnitId já faz parte da queryKey via
  // buildQueryKey — trocar de BU já produz chaves diferentes para o que de fato é
  // BU-scoped, sem precisar de um clear() geral; o clear() correto é só o de tenant, que
  // é o único caso onde dados de escopos DIFERENTES poderiam colidir.
  const lastTenantId = useRef<string>(scope.tenantId);
  if (lastTenantId.current !== scope.tenantId) {
    queryClient.clear();
    lastTenantId.current = scope.tenantId;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TenantScopeContext.Provider value={scope}>
        {children}
      </TenantScopeContext.Provider>
    </QueryClientProvider>
  );
}

/**
 * `useTenantQuery` — leitura com chave de cache prefixada por tenant/BU.
 * Espelha `useQuery`, mas constrói a `queryKey` via `buildQueryKey`, garantindo
 * isolamento por tenant (RNF 2.3).
 */
export function useTenantQuery<TData>(
  area: string,
  queryFn: () => Promise<TData>,
  options?: {
    params?: unknown;
    enabled?: boolean;
  } & Omit<UseQueryOptions<TData, Error, TData, TenantQueryKey>, "queryKey" | "queryFn">,
): UseQueryResult<TData, Error> {
  const scope = useTenantScope();
  const { params, ...rest } = options ?? {};
  const queryKey = buildQueryKey(area, scope.tenantId, scope.businessUnitId, params);
  return useQuery<TData, Error, TData, TenantQueryKey>({
    queryKey,
    queryFn,
    ...rest,
  });
}
